import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {
  ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS,
  ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS,
  BUSINESS_CASE_ACTIONS,
  type BusinessCaseAction,
  type BusinessCaseFacts,
  type BusinessCaseOnlineOffer,
  type BusinessCaseType,
} from '@prompt-bridge/shared/ai-assistant-core';

import type {ToBusinessCaseFactsInput} from '../types/ToBusinessCaseFactsInput';
import {compareBusinessCasePlannedActivities} from './compareBusinessCasePlannedActivities';
import {toAssistantBusinessCaseTimelineResult} from './toAssistantBusinessCaseTimeline';
import {toBusinessCasePaymentState} from './toBusinessCasePaymentState';
import {toBusinessCaseVehicles} from './toBusinessCaseVehicles';
import {toTaskBusinessCasePlannedActivity} from './toTaskBusinessCasePlannedActivity';
import {toUpcomingBusinessCaseTestDrivePlannedActivities} from './toUpcomingBusinessCaseTestDrivePlannedActivities';

const isBusinessCaseAction = (action: string): action is BusinessCaseAction =>
  BUSINESS_CASE_ACTIONS.some((candidate) => candidate === action);

const toSupportedBusinessCaseType = (
  type: ToBusinessCaseFactsInput['businessCase']['businessCaseType']
): BusinessCaseType =>
  match(type)
    .returnType<BusinessCaseType>()
    .with('SELLING', () => 'SELLING')
    .with('BUYING', 'PURCHASE_BROKERAGE', () => 'BUYING')
    .exhaustive();

const toBusinessCaseOnlineOffer = (
  input: ToBusinessCaseFactsInput
): BusinessCaseOnlineOffer | undefined => {
  const offerId = input.businessCase.offers[0]?.id;
  const firstSentAt = input.businessCase.onlineOfferFirstSentAt ?? undefined;
  const validTo = input.businessCase.offerValidTo ?? undefined;
  const openCount = input.businessCase.onlineOfferEngagement.openCount;
  const lastOpenedAt = input.businessCase.onlineOfferEngagement.lastOpenedAt ?? undefined;
  const hasOnlineOfferEvidence =
    isNotNil(offerId) ||
    isNotNil(firstSentAt) ||
    isNotNil(validTo) ||
    openCount > 0 ||
    isNotNil(lastOpenedAt);

  return hasOnlineOfferEvidence
    ? {
        ...(isNotNil(offerId) ? {offerId} : {}),
        ...(isNotNil(firstSentAt) ? {firstSentAt} : {}),
        ...(isNotNil(validTo) ? {validTo} : {}),
        openCount,
        ...(isNotNil(lastOpenedAt) ? {lastOpenedAt} : {}),
      }
    : undefined;
};

export function toBusinessCaseFacts(input: ToBusinessCaseFactsInput): BusinessCaseFacts {
  const fullTimelineResult = toAssistantBusinessCaseTimelineResult(
    input.activities ?? [],
    input.customerMessages ?? []
  );
  const timeline = fullTimelineResult.timeline.slice(0, ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS);
  const omittedTimelineEventCount =
    fullTimelineResult.omittedUnmappedEventCount +
    Math.max(0, fullTimelineResult.timeline.length - timeline.length);
  const fullPlannedActivities = [
    ...input.upcomingTasks.map(toTaskBusinessCasePlannedActivity),
    ...toUpcomingBusinessCaseTestDrivePlannedActivities(input.testDrives ?? [], input.evaluatedAt),
  ].sort(compareBusinessCasePlannedActivities);
  const plannedActivities = fullPlannedActivities.slice(
    0,
    ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS
  );
  const omittedPlannedActivityCount = Math.max(
    0,
    fullPlannedActivities.length - plannedActivities.length
  );
  const vehicleFacts = toBusinessCaseVehicles(
    input.businessCase,
    input.vehicle,
    input.canReadMargin,
    input.saleVehicle
  );
  const onlineOffer = toBusinessCaseOnlineOffer(input);
  const timelineComplete =
    isNotNil(input.activities) &&
    input.completeness.messagesLoaded &&
    omittedTimelineEventCount === 0;
  const incompleteChecklistItemCount =
    input.checklist?.tasks.filter((task) => task.isMandatory && !task.markedAsDone).length ?? null;
  const payments = toBusinessCasePaymentState(input.businessCase.businessCaseState, input.checkout);

  return {
    kind: 'businessCase',
    businessCaseId: input.businessCase.id,
    type: toSupportedBusinessCaseType(input.businessCase.businessCaseType),
    state: input.businessCase.businessCaseState,
    createdAt: input.businessCase.createdAt,
    updatedAt: input.businessCase.updatedAt ?? input.businessCase.createdAt,
    offerValidTo: input.businessCase.offerValidTo,
    onlineOfferFirstSentAt: input.businessCase.onlineOfferFirstSentAt,
    expectedPurchaseDate: vehicleFacts.purchaseVehicle?.expectedPurchaseOn ?? null,
    customerRequestedPrice: vehicleFacts.purchaseVehicle?.customerExpectedPrice?.amount ?? null,
    maximumBuyingPrice: vehicleFacts.purchaseVehicle?.maxPurchasePrice?.amount ?? null,
    incompleteChecklistItemCount,
    ...vehicleFacts,
    ...(isNotNil(onlineOffer) ? {onlineOffer} : {}),
    availableActions: input.actions?.actions.filter(isBusinessCaseAction) ?? [],
    timeline,
    plannedActivities,
    payments,
    completeness: {
      timelineComplete,
      omittedTimelineEventCount,
      plannedActivitiesComplete:
        input.completeness.testDrivesLoaded && omittedPlannedActivityCount === 0,
      omittedPlannedActivityCount,
      checklistComplete: input.completeness.checklistLoaded,
      paymentEvidenceComplete: input.completeness.checkoutLoaded,
      offerEvidenceComplete:
        isNotNil(input.businessCase.onlineOfferFirstSentAt) || timelineComplete,
      ...input.completeness,
    },
  };
}
